import { ChangeDetectorRef, TemplateRef, Component, OnInit, ViewChild, Input, OnChanges, SimpleChanges, OnDestroy, AfterViewInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatChipsModule } from '@angular/material/chips';
import { MatMenuModule } from '@angular/material/menu';
import { MatTabsModule, MAT_TABS_CONFIG } from '@angular/material/tabs';
import { MatRippleModule } from '@angular/material/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { CoreService } from '../../services/core.service';
import { LoaderService } from '../../services/loader.service';
import { DataService } from '../../services/data.service';
import { ScriptLoaderService } from '../../services/script-loader.service';
import { ContentSiteI, RssFeedI, GroupedWeeklySlotsI, RssFeedSlotDetailI, DetailedGroupedSlotsByDayI, DisplaySlotDetailI, WeeklySlotsI, SocialPlatformI } from '../../interfaces/core.interface';
import { DatetimepickerComponent } from '../../components/parts/datetimepicker/datetimepicker.component';
import { SocialAiRssFeedsFormComponent } from '../../components/forms/create-account/rss-feeds-form/rss-feeds-form.component';
import { DialogComponent } from '../../components/dialog/dialog.component';
import { SocialAiSchedulerFormComponent } from '../../components/forms/social-ai-scheduler-form/social-ai-scheduler-form.component';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';

type Post = {
  id: number;
  rss_title: string;
  domain_name?: string;
  image_url: string;
  post_status?: string;
  enable_repeat?: number;
  parent_post_id?: number;
  tag?: string;
  social_platform_title?: string;
  scheduled_for?: string | Date;
  rss_description?: string;
};

type Alert = {
  id: number;
  contentsite_id: number;
  alert_text: string;
  alert_type: string;
  active: number;
  platform: string;
  format: string;
  rss_img_url: string;
  rss_title: string;
  created_at: string | Date;
  Domain: string;
  posting_queue_id: number;
};

@Component({
  selector: 'app-social-ai',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatRadioModule,
    MatCheckboxModule,
    MatSelectModule,
    MatChipsModule,
    MatMenuModule,
    MatTabsModule,
    MatRippleModule,
    SocialAiRssFeedsFormComponent,
    SocialAiSchedulerFormComponent,
    MatTooltipModule,
    MatTableModule,
    MatPaginator,
    MatSortModule,
    MatTooltipModule
  ],

  providers: [
    { provide: MAT_TABS_CONFIG, useValue: { animationDuration: '0ms' } }
  ],
  templateUrl: './social-ai.component.html',
  styleUrl: './social-ai.component.scss'
})
export class SocialAiComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {

  @Input() companyID: string | null = null;

  @ViewChild('weeklySlotFormTmpl', { static: true }) weeklySlotFormTmpl!: TemplateRef<any>;
  @ViewChild('rssFeedFormTmpl', { static: true }) rssFeedFormTmpl!: TemplateRef<any>;
  @ViewChild('confirmDeleteTmpl', { static: true }) confirmDeleteTmpl!: TemplateRef<any>;
  @ViewChild('viewSlotsTmpl', { static: true }) viewSlotsTmpl!: TemplateRef<any>;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  maxSidebarCards = 3;
  
  // Flag to indicate if we are in a bulk delete operation (e.g., Clear All Slots)
  private isBulkDeleting = false;
  posts: Post[] = [];
  alerts: Alert[] = [];
  naPlaceholder = './assets/images/na.png';
  private readonly LATEST_POSTS_KEY = 'latestPosts';
  private readonly LATEST_ALERTS_KEY = 'latestAlerts';

  rssFeed: RssFeedI = {} as RssFeedI;
  weeklySlot: WeeklySlotsI | WeeklySlotsI[] = [] as WeeklySlotsI[];
  selectedSite: ContentSiteI | null = null;
  feedsForSelectedSite: RssFeedI[] = [];
  showRssFeeds: boolean = false;
  selectedRssFeed: RssFeedI | null = null;
  rawSelectedFeedSlots: RssFeedSlotDetailI[] = [];
  isLoadingFeeds: boolean = false;
  isLoadingSites: boolean = false;
  private subscriptions: Subscription = new Subscription();
  allSocialMediaSlotsForDisplay: Map<string, DetailedGroupedSlotsByDayI[]> = new Map();
  weekdaysOrdered: string[] = [
    'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
  ];
  selectOptions: {
    sites: ContentSiteI[],
    feeds: RssFeedI[]
  } = {
      sites: [],
      feeds: []
    };
  feedsWithSlots: GroupedWeeklySlotsI[] = [];
  socialPlatforms: SocialPlatformI[] = [];

  selectedFeed: RssFeedI | null = null;
  allFeedsForSelectedRss: any[] = [];
  facebookArticles: any[] = [];
  instagramArticles: any[] = [];
  isLoadingArticles: boolean = false;
  private lastLoadedCompanyId: number | null = null;

  // table for combined feeds
  rssFeedsDataSource = new MatTableDataSource<RssFeedI>([]);
  rssDisplayedColumns: string[] = ['id', 'site', 'feed', 'url', 'isApproved', 'actions'];

  constructor(
    public dialog: MatDialog,
    private router: Router,
    private dataService: DataService,
    private loaderService: LoaderService,
    private coreService: CoreService,
    private scriptLoader: ScriptLoaderService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private snackBar: MatSnackBar,
    private route: ActivatedRoute,
  ) { }

  ngOnInit(): void {
  if (this.companyID) {
    const cid = Number(this.companyID);

    // if (this.lastLoadedCompanyId !== cid) {
    //   this.lastLoadedCompanyId = cid;
    //   this.loadSitesForCompany(cid);
    // }

    this.coreService.getData(`/get_weekly_slots?companyID=${this.companyID}`).subscribe((data: GroupedWeeklySlotsI[]) => {
      this.feedsWithSlots = data || [];
    });

    this.coreService.getData(`/getSocialPlatforms?companyID=${this.companyID}`).subscribe((data: any) => {
      this.socialPlatforms = data.data || [];
    });
  }

  this.loadSidebarPosts();
  this.loadAlertsFromStorage();
}

  ngAfterViewInit(): void {
    if (this.paginator) {
      this.rssFeedsDataSource.paginator = this.paginator;
    }
    if (this.sort) {
      this.rssFeedsDataSource.sort = this.sort;
    }
  }

 ngOnChanges(changes: SimpleChanges): void {
  if (changes['companyID']) {

    const newCompanyId = changes['companyID'].currentValue;

    if (newCompanyId) {

      const cid = Number(newCompanyId);

      if (this.lastLoadedCompanyId !== cid) {
        this.lastLoadedCompanyId = cid;
        this.loadSitesForCompany(cid);
      }

    } else {
      this.selectOptions.sites = [];
      this.selectedSite = null;
      this.feedsForSelectedSite = [];
      this.showRssFeeds = false;
      this.selectedRssFeed = null;
      this.rawSelectedFeedSlots = [];
      this.allSocialMediaSlotsForDisplay.clear();
      this.rssFeedsDataSource.data = [];
    }
  }
}

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  // Load all sites, then all feeds for the company (for the table)
  private loadSitesForCompany(companyId: number, preserveSelectedSiteId?: number): void {
    this.isLoadingSites = true;
    this.coreService.getSites(companyId).subscribe({
      next: (data: any) => {
        this.selectOptions.sites = data.siteDomains || [];

        if (preserveSelectedSiteId) {
          this.selectedSite =
            this.selectOptions.sites.find(s => s.ID === preserveSelectedSiteId) || null;
        } else {
          this.selectedSite = null;
        }

        this.selectedRssFeed = null;
        this.rawSelectedFeedSlots = [];
        this.allSocialMediaSlotsForDisplay.clear();

        this.isLoadingSites = false;

        // Load all feeds across all sites for the combined table
        this.loadAllFeedsForCompany(companyId);
      },
      error: (err) => {
        console.error('Error loading sites:', err);
        this.selectOptions.sites = [];
        this.selectedSite = null;
        this.selectedRssFeed = null;
        this.rawSelectedFeedSlots = [];
        this.allSocialMediaSlotsForDisplay.clear();
        this.isLoadingSites = false;
        this.rssFeedsDataSource.data = [];
      }
    });
  }

  private loadAllFeedsForCompany(companyId: number): void {
    if (!this.selectOptions.sites || this.selectOptions.sites.length === 0) {
      this.rssFeedsDataSource.data = [];
      return;
    }

    this.isLoadingFeeds = true;

    const siteRequests = this.selectOptions.sites.map((site: ContentSiteI) =>
      this.coreService.getData(`/contentsiterssfeed/${site.ID}`).pipe(
        map((res: any) => {
          const feeds: RssFeedI[] = res.data || [];
          return feeds.map((f: RssFeedI) => ({
            ...f,
            siteID: site.ID,
            Domain: site.Domain
          }));
        }),
        catchError(err => {
          console.error('Error loading feeds for site', site.ID, err);
          return of([] as RssFeedI[]);
        })
      )
    );

    forkJoin(siteRequests).subscribe({
      next: (feedsBySite: RssFeedI[][]) => {
        const allFeeds = feedsBySite.flat();
        this.rssFeedsDataSource.data = allFeeds;
        this.isLoadingFeeds = false;

        if (this.paginator) {
          this.rssFeedsDataSource.paginator = this.paginator;
        }
        if (this.sort) {
          this.rssFeedsDataSource.sort = this.sort;
        }
      },
      error: (err) => {
        console.error('Error loading all feeds for company', err);
        this.rssFeedsDataSource.data = [];
        this.isLoadingFeeds = false;
      }
    });
  }

  isPlatformRegistered(platformKey: string): boolean {
    return this.socialPlatforms.some(p => p.platform === platformKey);
  }
  onFeedSelected(feed: RssFeedI): void {
    this.selectedRssFeed = feed;
    this.rawSelectedFeedSlots = [];
    this.allSocialMediaSlotsForDisplay.clear();

    const siteId = (feed as any).siteID;
    if (siteId && this.selectOptions.sites?.length) {
      this.selectedSite =
        this.selectOptions.sites.find(s => s.ID === siteId) || null;
    }

    this.coreService.getData(`/get_scheduler_details/${feed.id}`).subscribe((data: { totalRecords: number, results: RssFeedSlotDetailI[] }) => {
      this.rawSelectedFeedSlots = data.results || [];

      const platformMaps = new Map<string, Map<string, DetailedGroupedSlotsByDayI>>();

      this.rawSelectedFeedSlots.forEach(slot => {
        const dayIndex = slot.weekday;
        const feedName = slot.feed || this.selectedRssFeed?.feed || 'N/A';
        const platform = slot.platform;

        if (!platformMaps.has(platform)) {
          platformMaps.set(platform, new Map<string, DetailedGroupedSlotsByDayI>());
        }
        const currentPlatformMap = platformMaps.get(platform)!;

        if (!currentPlatformMap.has(feedName)) {
          currentPlatformMap.set(feedName, {
            feed: feedName,
            platform: platform,
            slotsByDay: {}
          });
        }
        const currentEntry = currentPlatformMap.get(feedName)!;

        if (!currentEntry.slotsByDay[dayIndex]) {
          currentEntry.slotsByDay[dayIndex] = [];
        }

        currentEntry.slotsByDay[dayIndex].push({ weekly_slot_id: slot.weekly_slot_id, time: slot.post_time });
      });

      platformMaps.forEach((feedGroupMap, platformName) => {
        const platformArray = Array.from(feedGroupMap.values());
        platformArray.forEach(entry => {
          for (const dayIndex in entry.slotsByDay) {
            entry.slotsByDay[dayIndex].sort((a, b) => {
              const timeA = a.time ?? '';
              const timeB = b.time ?? '';
              return timeA.localeCompare(timeB);
            });
          }
        });
        platformArray.sort((a, b) => {
          const feedA = a.feed ?? '';
          const feedB = b.feed ?? '';
          return feedA.localeCompare(feedB);
        });
        this.allSocialMediaSlotsForDisplay.set(platformName, platformArray);
      });

      this.cdr.detectChanges();
    });
  }

  onTimeChange(event: Event, entry: any, dayIndex: number, timeIndex: number, slotId: number) {
    const inputElement = event.target as HTMLInputElement;
    const newTimeRaw = inputElement.value;
    const oldTimeRaw = entry.slotsByDay[dayIndex][timeIndex].time;

    const timeRegex = /^(?:2[0-3]|[01]?[0-9]):(?:[0-5]?[0-9])$/;
    if (!timeRegex.test(newTimeRaw)) {
      this.coreService.showSnackbar('Incorrect time format. Please use the format HH:MM (e.g. 09:00 or 23:30');
      inputElement.value = oldTimeRaw.slice(0, 5);
      return;
    }

    if (newTimeRaw === oldTimeRaw.slice(0, 5)) {
      return;
    }

    const newTimeFormatted = newTimeRaw + ':00';

    entry.slotsByDay[dayIndex][timeIndex].time = newTimeFormatted;

    const payload = {
      post_time: newTimeFormatted,
      companyID: this.companyID ? Number(this.companyID) : null
    };

    const endpoint = `updateWeeklySlot/${slotId}`;

    this.coreService.postData(endpoint, payload).subscribe({
      next: res => {
      },
      error: err => {
        this.coreService.showSnackbar('An error occurred while updating the time. Please try again.');
        inputElement.value = oldTimeRaw.slice(0, 5);
        entry.slotsByDay[dayIndex][timeIndex].time = oldTimeRaw;
      }
    });
  }

  restrictToTimeFormat(event: KeyboardEvent): void {
    const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', ':'];
    const specialKeys = [
      'Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'End', 'Home'
    ];

    if (specialKeys.includes(event.key) || event.ctrlKey || event.metaKey) {
      return;
    }

    if (event.ctrlKey && (event.key === 'c' || event.key === 'v' || event.key === 'x')) {
      return;
    }

    if (!allowedKeys.includes(event.key)) {
      event.preventDefault();
    }

    const inputElement = event.target as HTMLInputElement;
    const currentValue = inputElement.value;

    if (event.key === ':' && currentValue.includes(':')) {
      event.preventDefault();
    }

    if (currentValue.length >= 5 && !specialKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

  getGroupedSlotDisplay(groupedSlots: string[][], dayIndex: number): string {
    if (groupedSlots && groupedSlots[dayIndex] && Array.isArray(groupedSlots[dayIndex]) && groupedSlots[dayIndex].length > 0) {
      return groupedSlots[dayIndex].join(', ');
    }
    return '-';
  }

  addNewRssFeed() {
    if (!this.companyID) {
      this.coreService.showSnackbar('Company ID is not set. Cannot add Category.');
      return;
    }
    this.rssFeed = { companyID: Number(this.companyID) } as unknown as RssFeedI;

    this.dialog.open(DialogComponent, {
      data: {
        content: this.rssFeedFormTmpl,
        header: 'Add New Category',
        cls: '!bg-slate-600 !text-white'
      },
      width: '500px',
      maxWidth: '100vw'
    });
  }

  getRssFeedTooltipMessage(status: string): string {
    if (status === 'Pending') {
      return 'The Category status is pending. You cannot add slots until it is approved.';
    } else if (status === 'Not Approved') {
      return 'The Category is not approved. You cannot add slots.';
    } else if (status === 'Approved') {
      return '';
    }
    return 'Unknown Category status.';
  }


  addNewWeeklySlot(rssFeedId: number, feedName: string, platformName: string) {
    if (!this.selectedRssFeed) {
      this.coreService.showSnackbar('Please select a Category to add slots.');
      return;
    }

    if (this.selectedRssFeed.isApproved !== 'Approved') {
      this.coreService.showSnackbar(this.getRssFeedTooltipMessage(this.selectedRssFeed.isApproved));
      return;
    }

    const platform = this.socialPlatforms.find(p =>
      p.platform.toLowerCase() === platformName.toLowerCase() &&
      p.site_id === this.selectedSite?.ID
    );

    if (!platform) {
      this.coreService.showSnackbar(`Could not find an approved ${platformName} account for the selected site.`);
      console.warn(`No platform "${platformName}" found for site_id=${this.selectedSite?.ID}`, this.socialPlatforms);
      return;
    }

    if (platform.isApproved !== 'Approved') {
      this.coreService.showSnackbar(`The social media platform "${platformName}" is not approved. You cannot add slots.`);
      return;
    }

    const socialPlatformId = platform.id;

    this.weeklySlot = this.weekdaysOrdered.map((_, index) => ({
      id: 0,
      rss_feed_id: rssFeedId,
      social_platform_id: socialPlatformId,
      companyID: Number(this.companyID),
      site_id: this.selectedSite?.ID ?? 0,
      weekday: index as 0 | 1 | 2 | 3 | 4 | 5 | 6,
      post_time: '',
      status: 'active'
    }));

    this.dialog.open(DialogComponent, {
      data: {
        content: this.weeklySlotFormTmpl,
        header: `Choose the hours this platform is allowed to auto-schedule posts.`,
        cls: '!bg-slate-600 !text-white',
        formData: this.weeklySlot
      },
      width: '650px',
      height: '700px',
      maxWidth: '100vw'
    });
  }

  editRssFeed(row: RssFeedI) {
    this.rssFeed = row;

    this.dialog.open(DialogComponent, {
      data: {
        content: this.rssFeedFormTmpl,
        header: 'Edit Category',
        cls: '!bg-slate-600 !text-white'
      },
      width: '500px',
      maxWidth: '100vw'
    });
  }

  viewSlotsForFeed(feed: RssFeedI): void {
    this.onFeedSelected(feed);
    this.dialog.open(DialogComponent, {
      data: {
        content: this.viewSlotsTmpl,
        header: `Auto-scheduling times for: "${feed.feed}"`,
        cls: '!bg-slate-600 !text-white'
      },
      width: '1100px',
      maxWidth: '100vw'
    });
  }


  openDeleteRssFeedConfirm(row: RssFeedI) {
    this.rssFeed = row;
    this.dialog.open(DialogComponent, {
      data: {
        content: this.confirmDeleteTmpl,
        header: 'Delete Category?',
        cls: 'bg-red-500 !text-white'
      }
    });
  }

  // deleteRssFeed(): void {
  //  this.coreService.deleteData('deleteRssFeed', String(this.rssFeed.id)).subscribe({
  //   next: () => {
  //    if (this.selectedSite) {
  //     this.loadSitesForCompany(Number(this.companyID), this.selectedSite.ID);
  //    }
  //    this.dialog.closeAll();
  //   }
  //  });
  // }

  deleteSlot(slotId: number) {
    this.coreService.deleteData(`deleteSlot`, String(slotId)).subscribe({
      next: () => {
        this.onSubmitClbk({ type: 'weeklySlot', data: null });
      }
    });
  }

  /**
   * Διαγράφει ένα slot χωρίς να κάνει callback για ανανέωση του UI.
   * @param slotId Το ID του slot που θέλουμε να διαγράψουμε.
   * @returns Το observable για το DELETE request.
   */
  private deleteSlotWithoutCallback(slotId: number) {
    return this.coreService.deleteData(`deleteSlot`, String(slotId));
  }

  onSubmitClbk(event: { type: string, data: any }): void {

    this.dialog.closeAll();

    if (event.type === 'rssFeed') {

      this.selectedRssFeed = event.data;

      const cid = this.companyID ? Number(this.companyID) : null;

      if (cid) {

        this.lastLoadedCompanyId = cid;

        if (this.selectedSite) {
          this.loadSitesForCompany(cid, this.selectedSite.ID);
        } else {
          this.loadSitesForCompany(cid);
        }
      }

    } else if (event.type === 'weeklySlot') {
      // If we are in a bulk delete operation, don't close and reopen the dialog
      if (this.isBulkDeleting) {
        // Just refresh the data for the currently selected feed
        if (this.selectedRssFeed) {
          this.onFeedSelected(this.selectedRssFeed);
        }
        return; // Exit early
      }

      // Normal flow for single slot operations
      if (this.selectedRssFeed) {
        this.onFeedSelected(this.selectedRssFeed);
        setTimeout(() => {
          // Check again if the dialog is still open to avoid reopening unnecessarily
          if (this.dialog.openDialogs.length === 0) {
            this.dialog.open(DialogComponent, {
              data: {
                content: this.viewSlotsTmpl,
                header: `Auto-scheduling times for: "${this.selectedRssFeed?.feed}"`,
                cls: '!bg-slate-600 !text-white'
              },
              width: '1100px',
              maxWidth: '100vw'
            });
          }
        }, 200);
      } else {
        this.coreService.getData('/get_weekly_slots').subscribe((data: GroupedWeeklySlotsI[]) => {
          this.feedsWithSlots = data || [];
        });
      }
    }
  }

  getPostingQueueForFeeds(event: MatSelectChange): void {

    this.selectedFeed = event.value;

    if (!this.selectedFeed || !this.selectedFeed.id) {
      this.allFeedsForSelectedRss = [];
      this.facebookArticles = [];
      this.instagramArticles = [];
      this.isLoadingArticles = false;
      return;
    }

    this.isLoadingArticles = true;

    const companyIdForRequest = this.companyID ? Number(this.companyID) : null;

    if (companyIdForRequest === null) {
      console.error("companyID is null, cannot fetch posting queue.");
      this.coreService.showSnackbar('Company ID is missing.');
      this.isLoadingArticles = false;
      return;
    }


    forkJoin({
      posts: this.coreService.getData(`/getPostingQueue/${companyIdForRequest}/${this.selectedFeed.id}`)
    }).subscribe({
      // next: ({ limit, posts }) => {
      next: ({ posts }) => {
        this.allFeedsForSelectedRss = posts || [];
        this.filterArticlesByPlatform();
        this.isLoadingArticles = false;

        // if (this.allFeedsForSelectedRss.length >= limit) {
        //  this.coreService.showSnackbar('You have reached the posting limit for this month.');
        // }
      },
      error: (error) => {
        console.error('Error fetching posting queue:', error);
        this.coreService.showSnackbar('Error fetching posting queue.');
        this.isLoadingArticles = false;
      }
    });
  }

  filterArticlesByPlatform(): void {
    this.facebookArticles = this.allFeedsForSelectedRss.filter(article =>
      article.social_platform_name && article.social_platform_name.toLowerCase().includes('facebook')
    );
    this.instagramArticles = this.allFeedsForSelectedRss.filter(article =>
      article.social_platform_name && article.social_platform_name.toLowerCase().includes('instagram')
    );
  }

  //Sidebar helpers
  private loadSidebarPosts(): void {
    if (typeof window === 'undefined') {
      this.posts = [];
      return;
    }

    const raw = localStorage.getItem(this.LATEST_POSTS_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as Post[] | null;
        if (Array.isArray(parsed)) {
          this.posts = parsed.slice(0, this.maxSidebarCards);
        } else {
          this.posts = [];
        }
      } catch (e) {
        console.error('Failed to parse latest posts from localStorage', e);
        this.posts = [];
      }
    }

    this.coreService.latestPosts$.subscribe(posts => {
      if (posts && posts.length > 0) {
        this.posts = posts.slice(0, this.maxSidebarCards);
      }
    });
  }

  private loadAlertsFromStorage(): void {
    if (typeof window === 'undefined') return;

    const raw = localStorage.getItem(this.LATEST_ALERTS_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          this.alerts = parsed.slice(0, this.maxSidebarCards);
        }
      } catch (e) {
        console.error('Failed to parse alerts from storage', e);
      }
    }

    this.coreService.latestAlerts$.subscribe(alerts => {
      if (alerts && alerts.length > 0) {
        this.alerts = alerts.slice(0, this.maxSidebarCards);
      }
    });
  }

  viewAlert(alert: Alert): void {
    const targetId = alert.posting_queue_id;
    this.router.navigate(['/preview-post', alert.format, targetId]);
  }

  viewRow(row: Post) {
    const targetId =
      row.enable_repeat === -1 ? row.parent_post_id ?? row.id : row.id;
    this.router.navigate(['/preview-post', row.tag, targetId]);
  }

  trackById = (_: number, item: Post) => item.id;

  trackByAlertId = (_: number, item: Alert) => item.id;

  onMainImgError(ev: Event): void {
    const img = ev.target as HTMLImageElement;
    if (!img || img.src.endsWith(this.naPlaceholder)) return;
    img.src = this.naPlaceholder;
  }

  platformTextClass(plat?: string) {
    const t = (plat || "").toLowerCase().trim();
    switch (t) {
      case "instagram":
        return "text-fuchsia-600";
      case "facebook":
        return "text-blue-600";
      case "x":
        return "text-neutral-700";
      case "youtube":
        return "text-red-600";
      default:
        return "text-gray-700";
    }
  }

  /**
   * Διαγράφει όλα τα slots για το επιλεγμένο feed για όλες τις μέρες.
   */
  clearAllSlotsForSelectedFeed(): void {
    if (!this.selectedRssFeed) {
      console.warn('No selected RSS feed to clear slots for.');
      return;
    }

    // Set the flag to indicate we are in a bulk delete operation
    this.isBulkDeleting = true;

    // Κάνουμε ένα μόνο DELETE request για να διαγράψουμε όλα τα slots
    this.coreService.deleteData('clearAllSlots', this.selectedRssFeed.id).subscribe({
      next: (response: any) => {
        console.log('Successfully deleted all slots:', response);
        // Reset the flag
        this.isBulkDeleting = false;
        // Ανανεώνουμε την προβολή μόνο μία φορά
        if (this.selectedRssFeed) {
          this.onFeedSelected(this.selectedRssFeed);
        }
      },
      error: (err) => {
        console.error('Error deleting slots:', err);
        // Reset the flag even on error
        this.isBulkDeleting = false;
        this.coreService.showSnackbar('An error occurred while deleting slots.');
        // Ακόμα και σε περίπτωση σφάλματος, προσπαθούμε να ανανεώσουμε την προβολή
        if (this.selectedRssFeed) {
          this.onFeedSelected(this.selectedRssFeed);
        }
      }
    });
  }

  /**
   * Έλεγχος αν υπάρχουν slots για το επιλεγμένο feed
   */
  hasSlots(): boolean {
    if (!this.allSocialMediaSlotsForDisplay || this.allSocialMediaSlotsForDisplay.size === 0) {
      return false;
    }

    // Ελέγχουμε αν υπάρχει τουλάχιστον ένα slot σε οποιαδήποτε πλατφόρμα/ημέρα
    for (const platformEntries of this.allSocialMediaSlotsForDisplay.values()) {
      for (const entry of platformEntries) {
        for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
          const slotsForDay = entry.slotsByDay[dayIndex];
          if (slotsForDay && slotsForDay.length > 0) {
            return true;
          }
        }
      }
    }

    return false;
  }

  /**
   * Αυτόματη δημιουργία slots για το επιλεγμένο feed
   */
  autoCreateSlots(): void {
    if (!this.selectedRssFeed || !this.companyID) {
      console.warn('No selected RSS feed or company ID.');
      this.coreService.showSnackbar('Unable to create slots: Missing feed or company information.');
      return;
    }

    // Κλείνουμε το dialog πριν κάνουμε το API call
    this.dialog.closeAll();

    // Κάνουμε το API call για δημιουργία slots μόνο για το συγκεκριμένο feed
    this.coreService.postData('autoWeeklySlots', { feed_id: this.selectedRssFeed.id }).subscribe({
      next: (response: any) => {
        console.log('Successfully created auto weekly slots:', response);
        this.coreService.showSnackbar('Slots created successfully!');
        
        // Ανανεώνουμε την προβολή για να δείξουμε τα νέα slots
        if (this.selectedRssFeed) {
          this.onFeedSelected(this.selectedRssFeed);
        }
        
        // Ανοίγουμε πάλι το dialog για να δείξουμε τα νέα slots
        setTimeout(() => {
          if (this.selectedRssFeed) {
            this.viewSlotsForFeed(this.selectedRssFeed);
          }
        }, 500);
      },
      error: (err) => {
        console.error('Error creating auto weekly slots:', err);
        this.coreService.showSnackbar('Error creating slots. Please try again.');
      }
    });
  }
}
